import { FormField, PanelSection, TextInput } from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import React from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { CountryAutocomplete } from 'src/core/common/components/CountryAutocomplete';

import { type FormValues } from './SupplierForm';

type Props = {
  values: FormValues;
  errors: FormikProps<FormValues>['errors'];
  handleChange: FormikProps<FormValues>['handleChange'];
  setFieldValue: FormikProps<FormValues>['setFieldValue'];
  isVatEnabled: boolean;
};

export const LegalInformationSection = ({
  values,
  errors,
  handleChange,
  setFieldValue,
  isVatEnabled,
}: Props) => {
  const { t } = useTranslation('global');

  return (
    <PanelSection title={t('submitMyInvoice.supplier.legalInfo')}>
      <FormField
        className="mt-16"
        label={t('submitMyInvoice.fields.supplier.legalName')}
        alertMessage={errors.legalName}
      >
        <TextInput
          name="legalName"
          placeholder={t('submitMyInvoice.fields.supplier.placeholder')}
          value={values.legalName ?? ''}
          isDisabled
          onChange={handleChange}
        />
      </FormField>

      <FormField
        className="mt-16"
        label={t('submitMyInvoice.fields.registrationNumber.label')}
        hint={t('misc.optional')}
      >
        <TextInput
          name="registrationNumber"
          placeholder={t(
            'submitMyInvoice.fields.registrationNumber.placeholder',
          )}
          value={values.registrationNumber ?? ''}
          onChange={handleChange}
        />
      </FormField>
      {isVatEnabled && (
        <FormField
          className="mt-16"
          label={t('submitMyInvoice.fields.vatNumber.label')}
          hint={t('misc.optional')}
        >
          <TextInput
            name="vatNumber"
            placeholder={t('submitMyInvoice.fields.vatNumber.placeholder')}
            value={values.vatNumber ?? ''}
            onChange={handleChange}
          />
        </FormField>
      )}

      <FormField
        className="mt-16"
        label={t('submitMyInvoice.fields.address.label')}
        hint={t('misc.optional')}
      >
        <TextInput
          name="address"
          className="mb-8"
          placeholder={t('submitMyInvoice.fields.address.addressPlaceholder')}
          value={values.address ?? ''}
          onChange={handleChange}
        />
        <TextInput
          name="zipcode"
          className="mb-8"
          placeholder={t('submitMyInvoice.fields.address.zipcodePlaceholder')}
          value={values.zipcode ?? ''}
          onChange={handleChange}
        />
        <TextInput
          className="mb-8"
          name="city"
          placeholder={t('submitMyInvoice.fields.address.cityPlaceholder')}
          value={values.city ?? ''}
          onChange={handleChange}
        />
        <CountryAutocomplete
          fit="parent"
          placeholder={t('submitMyInvoice.fields.address.countryPlaceholder')}
          countryCode={values.country ?? ''}
          onSelect={(countryCode) => {
            if (countryCode) {
              setFieldValue('country', countryCode);
            }
          }}
        />
      </FormField>
    </PanelSection>
  );
};
